import type { PaymentMethodsResponse } from '@adyen/adyen-web';
import { AxiosError } from 'axios';
import { differenceInSeconds, parse, startOfDay } from 'date-fns';
import { format } from 'date-fns-tz';
import { flatten, isNumber, sortBy } from 'lodash-es';
import { makeAutoObservable, reaction, runInAction, toJS } from 'mobx';
import { isHydrated, makePersistable } from 'mobx-persist-store';

import type { CartCalculationOrderResponse } from '~/api/Catalog';
import {
  DeliveryMechanic,
  DeliveryMethods,
  type DeliverySlot,
  type ETAAvailability,
  type ETACalculation,
  type ETACost,
  ETAPaymentMethod,
  ETARequests,
  isPhonePaymentMethod,
  slotToCalculation,
} from '~/api/ETA';
import {
  type AwsPayment,
  type FillFeedbackRequest,
  type GiftItem,
  type GiftMechanism,
  type NewOrderCartItemOffer,
  type NewOrderResponse,
  type OctoPaymentMethod,
  type Order,
  type OrderAddress,
  type OrderPublicStatus,
  OrderRequests,
  type OrderShort,
  type OrderStatus,
  type OrdersResponse,
  type Payment,
  PaymentSystem,
  type PaymentSystemItem,
  type VerifonePaymentCard,
} from '~/api/Order';
import type { ApiErrorResponse } from '~/api/Requests';
import { adaptV3NewToV2, pickV3NewOrderTotals } from '~/api/v3/adapters';
import { V3Requests } from '~/api/v3/requests';
import type {
  V3NewRequest,
  V3OrderBundleInput,
  V3OrderItemInput,
  V3PaymentInput,
} from '~/api/v3/types';
import { company } from '~/company/Company';
import { CompanyName } from '~/company/interface';
import { SECOND } from '~/constants/variables';
import { ACTIVE_ORDER_STATUS_CRITERIA } from '~/pages/Cabinet/constants';
import { AnalyticsEvent, analyticsService } from '~/services/analytics';
import { DOMAIN, REASON, reportFailure, trace } from '~/services/telemetry';
import {
  cartStore,
  checkoutStore,
  deliveryMethodAvailabilityStore,
  mainStore,
} from '~/stores';
import type { Offer } from '~/stores/CategoriesStore';
import AdditionalPaymentMethod from '~/stores/CheckoutStore/AdditionalPaymentMethod';
import MobilePaymentMethod from '~/stores/CheckoutStore/MobilePaymentMethod';
import type OrderData from '~/stores/CheckoutStore/OrderData';
import { splitAddressLine } from '~/utils/addressLine';
import { deleteCookie, setCookie } from '~/utils/cookies';
import {
  type DestinationKind,
  destinationKindForUsage,
} from '~/utils/destinationKind';

import { CURRENCY_SYMBOL } from '../constants';
import { DeliveryStore } from '../DeliveryStore';
import { NoWarehouseError } from '../DeliveryStore/NoWarehouseError';
import type { BaseStore } from '../interfaces/BaseStore';
import { storage as localStorage } from '../LocalStorage';
import type { UserStore } from '../UserStore';
import { OrderIdempotency } from './OrderIdempotency';
import { handleOrderError, OrderErrorHandled } from './parseOrderError';

/**
 * Delays before re-attempting a failed payment systems request.
 *
 * The checkout cannot initialise without the list, and nothing else asks for
 * it again once the page is up, so a single failed request has to be retried
 * here rather than left for the next navigation.
 */
const PAYMENT_SYSTEMS_RETRY_DELAYS = [1000, 3000];

export type OrderStatusStore =
  | 'none'
  | 'accepted'
  | 'picking'
  | 'readyToShip'
  | 'readyForPickup'
  | 'inDelivery'
  | 'delivered'
  | 'failed'
  | 'cancelled'
  | 'notCollected';

export type RequestETAResponse = {
  oldPrice: string;
  newPrice: string;
  etaCalculation?: Nullable<ETACalculation>;
};

export interface RateInfo {
  rating: number;
  deliveryRating?: number;
  productVarietyRating?: number;
  productQualityRating?: number;
  comment?: string;
}

export interface ExpressDeliveryLabelResult {
  message: string;
  args: Record<string, string>;
  showFlashIcon: boolean;
  expressDelivery?: ETAAvailability;
  slot?: DeliverySlot;
  /** Hard-coded default used when no ETA or slot is available */
  isFallback?: boolean;
}

export interface ExpressDeliveryLabelProps {
  timeFormat: string;
  locale: any;
  onlySlot?: boolean;
  onlyTime?: boolean;
}

const DEFAULT_FEES_COST: ETACost = {
  shipping: 0,
  threshold: 0,
  shippingPounds: '0',
  thresholdPounds: '0',
  serviceFee: 0,
  serviceFeeThreshold: 0,
  serviceFeeThresholdPounds: '0',
  serviceFeeShippingPounds: '0',
};

export class OrderStore implements BaseStore {
  etaCalculation: ETACalculation | null = null;
  etaSlotCalculation: ETACalculation | null = null;
  orderStatus: OrderStatusStore = 'none';
  orderList: OrderShort[] | null = null;
  /**
   * Order currently displayed on the order details page. Set by the page on
   * mount/update and consumed by layout-level components (e.g. the desktop
   * PageHeader) that need a dynamic title built from order data.
   */
  viewedOrder: Order | OrderShort | null = null;
  isFirstOrder: Nullable<boolean> = null;
  currentOrdersPage = 1;
  activeOrderID = '';
  activeOrderBonuses: number | null = null;
  lastRatedOrderID = '';
  activeOrderProductCount = 0;
  paymentCards: Payment[] = [];
  paymentMethods: PaymentMethodsResponse['paymentMethods'] = [];
  activeGift: GiftItem | null = null;
  gift: GiftMechanism | null = null;
  isAuthInCart = false;
  tipAmount = '';
  isRegularTips = false;
  regularTipsValue = '';
  isCalculateTips = false;
  currency = 'aed';
  paymentSystems = new Map<PaymentSystem, ETAPaymentMethod[]>();
  paymentMethodsMap = new Map<ETAPaymentMethod, PaymentSystem[]>();
  /**
   * Whether the payment systems request has answered in this session.
   *
   * `paymentSystems` is never persisted and starts empty, so its emptiness on
   * its own cannot separate "not fetched yet" from "nothing is offered here".
   * The checkout gate reads it as the second and refuses to initialise, which
   * leaves the page inert with no way back — so the difference is recorded
   * rather than inferred.
   */
  arePaymentSystemsLoaded = false;
  /** Whether a payment systems request, including its retries, is in flight. */
  isPaymentSystemsLoading = false;
  isETALoading = false;
  /**
   * Whether the active delivery address has no warehouse behind it.
   *
   * Kept apart from `etaCalculation`, which holds the last calculation that
   * succeeded and is deliberately not cleared when one fails — the catalogue
   * reads delivery times off it. That makes it useless as a signal: an address
   * whose zone was switched off keeps the previous warehouse's numbers, so the
   * checkout gate, the closed-warehouse guard and the cart button all still
   * see a working address while `calculate` fails and prices fall to zero.
   *
   * Never persisted: a stale copy of this in localStorage would block the
   * checkout for an address that works again.
   */
  isDeliveryUnavailable = false;
  isLoading = false;
  downloadIsPossible = false;
  url3Ds = '';
  onBackFromPayment: (() => Promise<void>) | null = null;

  readonly deliveryStore: DeliveryStore;

  /** Lifecycle manager for the create-order `Idempotency-Key`. */
  readonly idempotency: OrderIdempotency;

  private _ordersPollingEnabled: boolean = false;
  private _ordersPollingTimeoutRef: Nullable<ReturnType<typeof setTimeout>> =
    null;
  /**
   * The payment systems request currently in flight, keyed by delivery zone.
   *
   * Three call sites ask on mount — the request cycle, the checkout and its
   * content — and without this they fire three identical requests, each able
   * to overwrite the others' result out of order.
   */
  private _paymentSystemsRequest: Nullable<{
    key: string;
    promise: Promise<void>;
  }> = null;

  constructor(protected readonly userStore: UserStore) {
    makeAutoObservable(this);
    makePersistable(this, {
      name: 'OrderStore',
      properties: [
        'etaCalculation',
        'paymentCards',
        'activeGift',
        'gift',
        'lastRatedOrderID',
        'regularTipsValue',
        'isRegularTips',
        'currency',
      ],
      storage: localStorage,
    }).catch((error) => error && console.error(error));

    this.deliveryStore = new DeliveryStore();
    this.idempotency = new OrderIdempotency();

    reaction(
      () => this._ordersPollingEnabled,
      (arg) => {
        if (!arg) {
          this._clearOrdersPollingPayload();
          return;
        }

        this._startOrdersPolling();
      },
    );

    reaction(
      () => this.userStore.personalData.id,
      (id) => {
        if (!id) {
          return;
        }

        this.requestOrders();
      },
    );
  }

  get orderTipsValue(): string {
    if (checkoutStore.deliveryMethod === DeliveryMethods.CLICK_AND_COLLECT) {
      return '';
    }

    if (!this.isCalculateTips) {
      return '';
    }

    return this.regularTipsValue;
  }

  // Getters
  get isSynchronized(): boolean {
    return isHydrated(this);
  }

  getWHCode(): string | null {
    return this.etaCalculation?.warehouse.code || null;
  }

  get fee(): ETACost {
    return (
      this.etaSlotCalculation?.cost[checkoutStore.deliveryMethod] ??
      this.etaCalculation?.cost[checkoutStore.deliveryMethod] ??
      DEFAULT_FEES_COST
    );
  }

  get cartPriceForGift(): string {
    if (!this.gift) {
      return '';
    }
    if (!this.gift.minimum_order_amount) {
      return '';
    }
    const diff =
      mainStore.convertPoundsToPence(this.gift.minimum_order_amount) -
      mainStore.convertPoundsToPence(cartStore.finalPrice);
    return diff <= 0 ? '' : mainStore.convertPenceToPounds(diff);
  }

  get etaWarehouseCode(): string {
    if (!this.etaCalculation) {
      return company.config.warehouse.default;
    }
    return (
      (this.userStore.deliveryAddress && this.etaCalculation.warehouse.code) ||
      company.config.warehouse.default
    );
  }

  get isStoreAvailable() {
    return this.etaCalculation?.warehouse.availability.availableNow;
  }

  get whCode(): string {
    return this.etaCalculation?.warehouse.code ?? '';
  }

  get isPickupAvailable(): boolean {
    // Scoped ETA responses (delivery switcher) carry only the requested
    // method, so the last calculation cannot answer "is pickup available
    // here". The dedicated pickup probe can — consumers like the COLLECT
    // promocode validation rely on it being method-independent.
    if (company.showDeliveryMethodSwitcher) {
      return deliveryMethodAvailabilityStore.pickupAvailable;
    }

    return !!this.etaCalculation?.warehouse.deliveryMethods[
      DeliveryMethods.CLICK_AND_COLLECT
    ];
  }

  get currencySymbol(): string {
    return CURRENCY_SYMBOL[this.currency] ?? '';
  }

  get isExpressAvailableNow(): boolean {
    return !!this.etaCalculation?.warehouse?.availability?.deliveryMechanics
      ?.ON_DEMAND?.availableNow;
  }

  get finishedOrders() {
    return (
      this.orderList?.filter((i) =>
        ['delivered', 'failed', 'cancelled', 'not_collected'].includes(
          i.public_status,
        ),
      ) ?? []
    );
  }

  get activeOrders() {
    return (
      this.orderList?.filter(
        (i) =>
          !['delivered', 'failed', 'cancelled', 'not_collected'].includes(
            i.public_status,
          ),
      ) ?? []
    );
  }
  get slotDeliverySchedule() {
    const getSecondsSinceStartOfDay = (date: number) => {
      const start = startOfDay(date);
      return differenceInSeconds(date, start);
    };

    const sortedSlots = flatten(
      sortBy(Object.entries(checkoutStore.deliverySlots), [
        ([time]) => Number(time),
      ])
        .map((entry) => toJS(entry[1]))
        .filter((slots) => Boolean(slots.length)),
    );
    if (sortedSlots.length === 0) {
      return null;
    }
    const { min_time_string, max_time_string } = sortedSlots.reduce(
      (
        acc,
        {
          delivery_min_time,
          delivery_max_time,
          delivery_min_time_string,
          delivery_max_time_string,
        },
      ) => {
        const min_time = getSecondsSinceStartOfDay(delivery_min_time * 1000);
        const max_time = getSecondsSinceStartOfDay(delivery_max_time * 1000);

        if (acc.min_time === Infinity || min_time < acc.min_time) {
          acc.min_time = min_time;
          acc.min_time_string =
            delivery_min_time_string.split(',')[2]?.trim() || '';
        }
        //Use latest time of same day
        if (max_time > acc.max_time) {
          acc.max_time = max_time;
          acc.max_time_string =
            delivery_max_time_string.split(',')[2]?.trim() || '';
        }

        return acc;
      },
      {
        min_time: Infinity,
        max_time: -Infinity,
        min_time_string: '00:00',
        max_time_string: '23:59',
      },
    );

    return {
      opening: min_time_string,
      closing: max_time_string,
    };
  }
  get closestSlot() {
    return sortBy(Object.entries(checkoutStore.deliverySlots), [
      ([time]) => Number(time),
    ])
      .map((entry) => entry[1])
      .find((slots) => Boolean(slots.length))?.[0];
  }

  get warehouseAvailability() {
    return this.etaCalculation?.warehouse.availability;
  }

  get expressDelivery() {
    return this.etaCalculation?.warehouse.availability.deliveryMechanics?.[
      DeliveryMechanic.ON_DEMAND
    ];
  }

  get scheduledDelivery() {
    return this.etaCalculation?.warehouse.availability.deliveryMechanics?.[
      DeliveryMechanic.SCHEDULED
    ];
  }

  getDeliveryLabel({
    timeFormat,
    locale,
    onlySlot,
    onlyTime,
  }: ExpressDeliveryLabelProps): ExpressDeliveryLabelResult {
    const defaultLabel = this.getDefaultDeliveryLabel();
    const weekdayFormat = company.useShortWeekdayDeliveryLabel ? 'EEE' : 'EEEE';

    if (!this.etaCalculation) {
      return defaultLabel;
    }

    const isPickup =
      checkoutStore.deliveryMethod === DeliveryMethods.CLICK_AND_COLLECT;
    const onDemandDeliveryMechanic = this.expressDelivery;

    const isExpressDeliveryAvailable = Boolean(
      onDemandDeliveryMechanic && onDemandDeliveryMechanic.availableNow,
    );

    const closestDeliverySlot = this.closestSlot;

    if (onlySlot || (!isExpressDeliveryAvailable && closestDeliverySlot)) {
      const slot = closestDeliverySlot;

      if (!slot) {
        return defaultLabel;
      }

      const minTimeSlices = slot.delivery_min_time_string.split(', ');
      const parsedDateMin = parse(
        slot.delivery_min_time_string,
        'dd-MM-yyyy, EEEE, HH:mm',
        new Date(),
      );
      const maxTimeSlices = slot.delivery_max_time_string.split(', ');
      const parsedDateMax = parse(
        slot.delivery_max_time_string,
        'dd-MM-yyyy, EEEE, HH:mm',
        new Date(),
      );

      if (!minTimeSlices.length || !maxTimeSlices.length) {
        return defaultLabel;
      }

      return {
        message: 'phrases:slotDeliveryBannerTitle',
        args: {
          time: `${format(
            parsedDateMin,
            onlyTime ? timeFormat : `${weekdayFormat} ${timeFormat}`,
            {
              timeZone: company.config.timeZone,
              locale,
            },
          )}\u00a0-\u00a0${format(parsedDateMax, timeFormat, {
            timeZone: company.config.timeZone,
            locale,
          })}`,
          date: format(parsedDateMin, weekdayFormat, {
            timeZone: company.config.timeZone,
            locale,
          }),
        },
        showFlashIcon: false,
        slot,
      };
    }

    const { duration } = this.etaCalculation || {};

    if (!closestDeliverySlot && !duration.min && !duration.max) {
      return defaultLabel;
    }

    return {
      message: defaultLabel.message,
      args: {
        time: duration.range,
      },
      showFlashIcon: !isPickup,
      expressDelivery: onDemandDeliveryMechanic,
    };
  }

  /**
   * Delivery label used when `etaCalculation` is not available yet (SSR,
   * first client render before mount, or before the ETA response arrives).
   * Exposed separately so mount-gated consumers can render the exact same
   * fallback the server produced, avoiding a hydration mismatch.
   */
  getDefaultDeliveryLabel(): ExpressDeliveryLabelResult {
    const isPickup =
      checkoutStore.deliveryMethod === DeliveryMethods.CLICK_AND_COLLECT;

    return {
      message: isPickup
        ? 'phrases:pickupBannerTitle'
        : 'phrases:expressDeliveryBannerTitle',
      args: { time: '30' },
      showFlashIcon: !isPickup,
      isFallback: true,
    };
  }

  // Setters
  setIsFirstOrder(flag: boolean) {
    this.isFirstOrder = flag;
  }

  setEtaCalculation(val: ETACalculation | null) {
    this.etaCalculation = val;

    if (typeof window !== 'undefined') {
      val?.warehouse.code && this.userStore.deliveryAddress
        ? setCookie('warehouse', val.warehouse.code, { 'max-age': 2678400 })
        : deleteCookie('warehouse');
    }
  }

  setEtaSlotCalculation(val: ETACalculation | null) {
    this.etaSlotCalculation = val;
  }

  setIsLoading(val: boolean) {
    this.isLoading = val;
  }

  setOrderStatus(val: OrderPublicStatus | 'none') {
    let status: OrderStatusStore;
    switch (val) {
      case 'in_delivery':
        status = 'inDelivery';
        break;
      case 'ready_to_ship':
        status = 'readyToShip';
        break;
      case 'ready_for_pickup':
        status = 'readyForPickup';
        break;
      case 'not_collected':
        status = 'notCollected';
        break;
      default:
        status = val;
        break;
    }
    this.orderStatus = status;
    analyticsService.track(AnalyticsEvent.ORDER_STATUS_CHANGED, {
      status: val,
      mapped_status: status,
    });
  }

  setActiveOrderID(val: string) {
    this.activeOrderID = val;
  }

  setViewedOrder(order: Order | OrderShort | null) {
    this.viewedOrder = order;
  }

  setActiveOrderBonuses(val: number | null) {
    this.activeOrderBonuses = val;
  }

  setLastRatedOrderID(val: string) {
    this.lastRatedOrderID = val;
  }

  setActiveOrderProductCount(val: number) {
    this.activeOrderProductCount = val;
  }

  setPaymentCards(arr: Payment[]) {
    this.paymentCards = arr;
  }

  setGift(val: GiftMechanism | null) {
    this.gift = val;
  }

  setActiveGift(val: GiftItem | null) {
    this.activeGift = val;
  }

  setIsAuthInCart(flag: boolean) {
    this.isAuthInCart = flag;
  }

  setCurrentOrdersPage(val: number) {
    this.currentOrdersPage = val;
  }
  setDownloadIsPossible(val: boolean) {
    this.downloadIsPossible = val;
  }

  setTipAmount(val: string) {
    this.tipAmount = val;
  }

  setCurrency(val: string) {
    this.currency = (val || 'aed').toLowerCase();
  }

  setPaymentSystems(systems: PaymentSystemItem[]) {
    this.paymentSystems = new Map(
      systems.map((item) => [item.system, item.methods]),
    );

    const paymentMethodsMap = new Map<ETAPaymentMethod, PaymentSystem[]>();
    for (const { system, methods } of systems) {
      for (const method of methods) {
        const list = paymentMethodsMap.get(method) ?? [];
        list.push(system);
        paymentMethodsMap.set(method, list);
      }
    }
    this.paymentMethodsMap = paymentMethodsMap;
  }

  setIsETALoading(val: boolean) {
    this.isETALoading = val;
  }

  setIsDeliveryUnavailable(val: boolean) {
    this.isDeliveryUnavailable = val;
  }

  setArePaymentSystemsLoaded(val: boolean) {
    this.arePaymentSystemsLoaded = val;
  }

  setIsPaymentSystemsLoading(val: boolean) {
    this.isPaymentSystemsLoading = val;
  }

  set3dsUrl(val: string) {
    this.url3Ds = val;
  }

  setOrdersPollingEnabled(val: boolean) {
    this._ordersPollingEnabled = val;
  }

  setOnBackFromPayment(handler: (() => Promise<void>) | null) {
    this.onBackFromPayment = handler;
  }

  // Actions
  getOrderById(id: string): OrderShort | null {
    if (!this.orderList) {
      return null;
    }
    for (let i = 0; i < this.orderList.length; i++) {
      // @ts-expect-error FIXME: migrate to noUncheckedIndexedAccess: true
      if (this.orderList[i].id === id) {
        // @ts-expect-error FIXME: migrate to noUncheckedIndexedAccess: true
        return this.orderList[i];
      }
    }
    return null;
  }

  calcOrderItems(order: OrderShort): OrderShort {
    const itemsCount = {
      actual: 0,
      requested: 0,
    };
    for (let i = 0; i < order.items.length; i++) {
      itemsCount.actual +=
        // @ts-expect-error FIXME: migrate to noUncheckedIndexedAccess: true
        order.items[i].actual_quantity ?? order.items[i].requested_quantity;
      // @ts-expect-error FIXME: migrate to noUncheckedIndexedAccess: true
      itemsCount.requested += order.items[i].requested_quantity;
    }
    order.itemsCount = itemsCount;
    return order;
  }

  // Requests API
  fetchETA(
    pos: string,
    deliveryMethod?: DeliveryMethods,
    opts?: {
      receivePrioritizedDeliveryMethod?: boolean;
      signal?: AbortSignal;
      destinationKind?: DestinationKind;
      skipSideEffects?: boolean;
    },
  ): Promise<ETACalculation> {
    this.setIsETALoading(true);

    return this.deliveryStore
      .requestETA(pos, deliveryMethod, opts)
      .then((etaResponse) => {
        runInAction(() => {
          mainStore.sendToRN('sendTags', {
            warehouse_code: company.useDefaultWarehouseWhenMissing
              ? (etaResponse.warehouse.code ?? company.config.warehouse.default)
              : etaResponse.warehouse.code,
          });
        });

        return etaResponse;
      })
      .finally(() => {
        this.setIsETALoading(false);
      });
  }

  /**
   * Fetches an ETA for a single method/warehouse without touching global
   * loading state or sending RN tags. Used by the availability probes so they
   * do not flicker the app-wide ETA-loading indicator.
   */
  probeETA(
    pos: string,
    deliveryMethod?: DeliveryMethods,
    opts?: { signal?: AbortSignal; destinationKind?: DestinationKind },
  ): Promise<ETACalculation> {
    return this.deliveryStore.requestETA(pos, deliveryMethod, {
      ...opts,
      skipSideEffects: true,
    });
  }

  async updateOrdersAnalytics() {
    if (!this.userStore.personalData.id) {
      return;
    }
    try {
      const { orders, total_count } = await OrderRequests.getOrders({
        filter: {
          customer_id: this.userStore.personalData.id,
        },
      });
      let successfulOrders: OrdersResponse;
      if (total_count) {
        successfulOrders = await OrderRequests.getOrders({
          filter: {
            customer_id: this.userStore.personalData.id,
            status: [
              'created',
              'reserved',
              'confirmed',
              'delivery_created',
              'wh_created',
              'picking',
              'ready_to_ship',
              'in_delivery',
              'in_logistic',
              'delivered',
              'delivered_partially',
              'finished',
            ],
          },
        });
      }
      runInAction(() => {
        mainStore.analytics.totalSpent = mainStore.convertPenceToPounds(
          (successfulOrders?.orders || []).reduce(
            (sum, order) => sum + (order.paid_total || 0),
            0,
          ),
        );
        mainStore.analytics.totalOrders = total_count;
        mainStore.analytics.totalSuccessfulOrders =
          successfulOrders?.total_count || 0;
        if (orders.length) {
          // @ts-expect-error FIXME: migrate to noUncheckedIndexedAccess: true
          mainStore.analytics.lastOrderCreatedAt = orders[0].created_at;
          mainStore.analytics.firstOrderCreatedAt =
            // @ts-expect-error FIXME: migrate to noUncheckedIndexedAccess: true
            orders[orders.length - 1].created_at;
        }
        mainStore.sendToRN('setUserProperties', {
          'Commerce: total spent': mainStore.analytics.totalSpent,
        });
        mainStore.sendToRN('setUserProperties', {
          'Commerce: total orders': total_count,
        });
        if (orders.length) {
          mainStore.sendToRN('setUserProperties', {
            'Commerce: first order date':
              mainStore.analytics.firstOrderCreatedAt,
          });
        }
      });
    } catch (error) {
      error && console.error(error);
    }
  }

  async requestETA(): Promise<RequestETAResponse> {
    this.setIsETALoading(true);

    try {
      return await this.calculateETA();
    } finally {
      /**
       * Kept in `finally` so a rejected ETA (e.g. "No warehouse found") cannot
       * leave the flag raised — it gates the geolocation and checkout buttons,
       * which would otherwise stay disabled for the rest of the session.
       */
      this.setIsETALoading(false);
    }
  }

  private async calculateETA(): Promise<RequestETAResponse> {
    // Deliberately not `?? warehouse.location`: falling back for an address
    // that exists but carries no coordinates would price the cart against the
    // company's own warehouse and answer successfully, so the checkout would
    // let an order through for a point nothing was ever checked against.
    // UserStore refuses to hold such a record, so this cannot be reached with
    // one.
    const { lat, lng } = this.userStore.deliveryAddress
      ? this.userStore.deliveryAddress.coordinates
      : company.config.warehouse.location;

    const scopedMethod = company.showDeliveryMethodSwitcher
      ? checkoutStore.deliveryMethod
      : undefined;

    try {
      const etaCalculation = await this.deliveryStore.requestETA(
        `${lat},${lng}`,
        scopedMethod,
      );

      // The user may have switched the delivery method while this scoped
      // request was in flight; applying the stale response would overwrite
      // state computed for the wrong method. Retry instead of bailing: the
      // method-change reaction skips while isETALoading is true (guaranteed
      // during this race), so nothing else would request the new method until
      // the next poll tick. `return await` keeps isETALoading true until the
      // retry settles. Recursion is bounded by actual user switches.
      if (
        scopedMethod !== undefined &&
        checkoutStore.deliveryMethod !== scopedMethod
      ) {
        return await this.requestETA();
      }

      const costMethod = scopedMethod ?? DeliveryMethods.COURIER_DELIVERY;

      const output: Pick<RequestETAResponse, 'oldPrice' | 'newPrice'> = {
        oldPrice:
          this.etaCalculation?.cost[checkoutStore.deliveryMethod]
            ?.shippingPounds ?? '0',
        newPrice:
          etaCalculation.cost[checkoutStore.deliveryMethod]?.shippingPounds ??
          '0',
      };

      checkoutStore.setDeliverySlots(etaCalculation.deliverySlots ?? null);

      runInAction(() => {
        if (
          this.etaCalculation?.highDemand !== etaCalculation.highDemand ||
          this.etaCalculation?.cost[costMethod]?.shipping !==
            etaCalculation.cost[costMethod]?.shipping ||
          this.etaCalculation?.cost[costMethod]?.threshold !==
            etaCalculation.cost[costMethod]?.threshold ||
          this.etaCalculation?.duration.range !== etaCalculation.duration.range
        ) {
          analyticsService.track(AnalyticsEvent.GENERAL_ETA_CHANGED, {
            eta_min: etaCalculation.duration.min ?? 0,
            eta_max: etaCalculation.duration.max ?? 0,
            delivery_fee: etaCalculation.cost[costMethod]?.shippingPounds ?? 0,
            threshold: etaCalculation.cost[costMethod]?.thresholdPounds ?? 0,
            is_surger: etaCalculation.highDemand ?? false,
          });
        }
        this.setEtaCalculation(etaCalculation);
        this.setIsDeliveryUnavailable(false);
        checkoutStore.setDeliveryPrices(output);
        if (output.oldPrice !== output.newPrice) {
          checkoutStore.setIsUpdatedDeliveryPopover(true);
        }
        mainStore.sendToRN('sendTags', {
          warehouse_code: etaCalculation.warehouse.code,
        });
        mainStore.sendToRN('setUserProperties', {
          'Address: warehouse code': etaCalculation.warehouse.id,
        });
      });

      return {
        ...output,
        etaCalculation: {
          ...this.etaCalculation,
          paymentMethods: etaCalculation.paymentMethods,
          warehouse: etaCalculation.warehouse,
        } as ETACalculation,
      };
    } catch (error) {
      // A scoped ETA request rejects when the selected delivery method has no
      // warehouse at this point. Keep the last known calculation and let the
      // availability probes drive a method switch — never leave the app-wide
      // ETA-loading flag stuck true (that would freeze the catalog forever).
      // Record the reason though: kept alone, the stale calculation says the
      // address still works, and everything gating the checkout believes it.
      // Only for an unscoped request — with a switcher the rejection means
      // "not by this method", which the probes resolve by switching, and
      // treating it as "not to this address" would block a checkout the other
      // method can serve.
      if (error instanceof NoWarehouseError && scopedMethod === undefined) {
        runInAction(() => {
          this.setIsDeliveryUnavailable(true);
        });
      }
      error && console.error(error);
      return {
        oldPrice: '0',
        newPrice: '0',
        etaCalculation: this.etaCalculation,
      };
    } finally {
      this.setIsETALoading(false);
    }
  }

  requestETASlot(): Promise<RequestETAResponse> {
    if (!this.userStore.deliveryAddress) {
      return Promise.reject();
    }
    this.setIsETALoading(true);
    return ETARequests.getETASlot({
      pos: `${this.userStore.deliveryAddress.coordinates.lat},${this.userStore.deliveryAddress.coordinates.lng}`,
      seller: 'JIFFY',
      ...(company.hasDeliveryTypeSwitcher
        ? {
            destinationKind: destinationKindForUsage(
              this.userStore.deliveryAddress.usage,
            ),
          }
        : {}),
      ...(checkoutStore.selectedDeliverySlot?.schedule_slot_id
        ? {
            deliverySlotId:
              checkoutStore.selectedDeliverySlot?.schedule_slot_id,
          }
        : {
            scheduleSlotId:
              checkoutStore.selectedDeliverySlot?.schedule_slot_id,
            currentDate: +(checkoutStore.selectedDeliveryDate ?? 0) / 1000,
          }),
    })
      .then(({ data }) => {
        const etaCalculation = slotToCalculation(data, (pence) =>
          mainStore.convertPenceToPounds(pence),
        );
        const output: RequestETAResponse = {
          oldPrice:
            this.etaCalculation?.cost[checkoutStore.deliveryMethod]
              ?.shippingPounds ?? '0',
          newPrice:
            etaCalculation.cost[checkoutStore.deliveryMethod]?.shippingPounds ??
            '0',
        };

        if (!etaCalculation || !Object.keys(etaCalculation.cost).length) {
          return Promise.reject({});
        }

        runInAction(() => {
          if (
            this.etaCalculation?.highDemand !== etaCalculation.highDemand ||
            this.etaCalculation?.cost[DeliveryMethods.COURIER_DELIVERY]
              ?.shipping !==
              etaCalculation.cost[DeliveryMethods.COURIER_DELIVERY]?.shipping ||
            this.etaCalculation?.cost[DeliveryMethods.COURIER_DELIVERY]
              ?.threshold !==
              etaCalculation.cost[DeliveryMethods.COURIER_DELIVERY]
                ?.threshold ||
            this.etaCalculation?.duration.range !==
              etaCalculation.duration.range
          ) {
            analyticsService.track(AnalyticsEvent.GENERAL_ETA_CHANGED, {
              eta_min: etaCalculation.duration.min ?? 0,
              eta_max: etaCalculation.duration.max ?? 0,
              delivery_fee:
                etaCalculation.cost[DeliveryMethods.COURIER_DELIVERY]
                  ?.shippingPounds ?? 0,
              threshold:
                etaCalculation.cost[DeliveryMethods.COURIER_DELIVERY]
                  ?.thresholdPounds ?? 0,
              is_surger: etaCalculation.highDemand ?? false,
            });
          }
          this.setEtaSlotCalculation(etaCalculation);
          // checkoutStore.setDeliverySlots(data.deliverySlots || null);
          checkoutStore.setDeliveryPrices(output);
          if (output.oldPrice !== output.newPrice) {
            checkoutStore.setIsUpdatedDeliveryPopover(true);
          }
          mainStore.sendToRN('sendTags', {
            warehouse_code: data.warehouse.code,
          });
          mainStore.sendToRN('setUserProperties', {
            'Address: warehouse code': data.warehouse.id,
          });

          if (!checkoutStore.selectedDeliveryDate) {
            checkoutStore.setSelectedDate('0');
            checkoutStore.setSelectedSlot(null);
          } else if (checkoutStore.selectedDeliveryDate !== '0') {
            const selectedDay =
              checkoutStore.deliverySlots[checkoutStore.selectedDeliveryDate];

            if (selectedDay) {
              let selectedSlot = selectedDay.find(
                (slot) =>
                  slot.schedule_slot_id ===
                  checkoutStore.selectedDeliverySlot?.schedule_slot_id,
              );
              if (!selectedSlot) {
                selectedSlot = checkoutStore.selectedDeliverySlot
                  ? selectedDay.find(
                      (slot) =>
                        slot.delivery_min_time >=
                        checkoutStore.selectedDeliverySlot!.delivery_max_time,
                    )
                  : selectedDay[0];
              }
            } else {
              const defaultDay = Object.entries(
                checkoutStore.deliverySlots || {},
              ).find(
                (i) =>
                  +i[0] > +(checkoutStore.selectedDeliveryDate || 0) &&
                  i[1].length > 0,
              );
              if (defaultDay) {
                checkoutStore.setSelectedDate(defaultDay[0]);
                // @ts-expect-error FIXME: migrate to noUncheckedIndexedAccess: true
                checkoutStore.setSelectedSlot(defaultDay[1][0]);
              }
            }
          }
        });
        return Promise.resolve(output);
      })
      .catch(async (error) => {
        console.error(error);
        if (
          !error.response &&
          (error.code === 'ECONNABORTED' || error.message === 'Network Error')
        ) {
          return Promise.reject();
        }

        await this.requestETA();
        return Promise.reject();
      })
      .finally(() => this.setIsETALoading(false));
  }

  async newOrder(data: OrderData): Promise<NewOrderResponse | null> {
    if (!data.phone) {
      return null;
    }

    this.isLoading = true;
    /**
     * Code below provides bonuses info for backend
     * "paidBonuses", "promocodeDiscountAmount" are required fields for new order request,
     * and they should be presented in each cart item
     * */
    const calculatedItemsMap: Record<string, Record<string, number>> = {};
    const calculatedItems =
      cartStore.deliveriesOrders.flatMap(({ items }) => items) ?? [];
    for (
      let calculatedItemIndex = 0;
      calculatedItemIndex < calculatedItems.length;
      calculatedItemIndex += 1
    ) {
      // @ts-expect-error FIXME: migrate to noUncheckedIndexedAccess: true
      const sku = calculatedItems[calculatedItemIndex].sku;
      calculatedItemsMap[sku] = {
        // @ts-expect-error FIXME: migrate to noUncheckedIndexedAccess: true
        paidBonuses: calculatedItems[calculatedItemIndex].paid_bonuses,
        promocodeDiscountAmount:
          // @ts-expect-error FIXME: migrate to noUncheckedIndexedAccess: true
          calculatedItems[calculatedItemIndex].promocode_discount_amount,
        // @ts-expect-error FIXME: migrate to noUncheckedIndexedAccess: true
        paidPrice: calculatedItems[calculatedItemIndex].paid_price,
        // @ts-expect-error FIXME: migrate to noUncheckedIndexedAccess: true
        discountAmount: calculatedItems[calculatedItemIndex].discount_amount,
        // @ts-expect-error FIXME: migrate to noUncheckedIndexedAccess: true
        basePrice: calculatedItems[calculatedItemIndex].base_price,
      };
    }

    const payment_method = Object.values(MobilePaymentMethod).includes(
      checkoutStore.activePaymentMethod as MobilePaymentMethod,
    )
      ? ETAPaymentMethod.Card
      : checkoutStore.activePaymentMethod === AdditionalPaymentMethod.KNET ||
          checkoutStore.activePaymentMethod ===
            AdditionalPaymentMethod.VisaMastercard
        ? ETAPaymentMethod.Card
        : isPhonePaymentMethod(
              checkoutStore.activePaymentMethod as ETAPaymentMethod,
            )
          ? (checkoutStore.activePaymentMethod as ETAPaymentMethod)
          : checkoutStore.activePaymentMethod === ETAPaymentMethod.QR
            ? ETAPaymentMethod.QR
            : (checkoutStore.activePaymentMethod as ETAPaymentMethod);

    const payment_system = checkoutStore.activePaymentSystem;

    const payment_method_id =
      checkoutStore.activePaymentMethod === AdditionalPaymentMethod.KNET
        ? 1
        : checkoutStore.activePaymentMethod ===
            AdditionalPaymentMethod.VisaMastercard
          ? 2
          : undefined;

    const session_id =
      !payment_method_id && payment_method === ETAPaymentMethod.Card
        ? data.sessionId
        : undefined;

    try {
      const idempotencyKey = this.idempotency.ensure();

      // A multi-warehouse cart is split by the backend into one calculation order
      // per partner warehouse, while the checkout keeps showing — and sending —
      // a single delivery, which the backend splits again on its side. Its totals
      // must come from the cart-wide values, the only ones that include the
      // delivery group price.
      const groupDeliveryPrice = cartStore.totalCartPrice.deliveryGroupPrice;
      const isGroupedDelivery =
        groupDeliveryPrice !== undefined &&
        checkoutStore.deliveries?.length === 1 &&
        cartStore.deliveriesOrders.length > 1;

      if (company.hasNewLogistics) {
        return await this.newOrderV3(
          data,
          payment_method,
          payment_system,
          payment_method_id,
          session_id,
        );
      }

      const deliveries =
        checkoutStore.deliveries?.map(
          ({ slotDeliveryDetails, itemsIds }, index) => {
            const order = cartStore.deliveriesOrders[index];

            if (!order) {
              throw new Error('Order not found');
            }

            const deliveryOrders = isGroupedDelivery
              ? cartStore.deliveriesOrders
              : [order];

            const {
              base_total,
              discount_total,
              is_delivery_free,
              paid_bonuses,
              paid_total,
              promocode,
              promocodeError,
              promocode_discount,
              service_fee,
              should_use_loyalty_points,
              should_use_bonuses,
              delivery_info: { paid_price },
            } = order;

            const items: NewOrderCartItemOffer[] = [];

            itemsIds.forEach((id) => {
              const cartItem = cartStore.selectedCartItems.find(
                (item) => item.sku === id,
              );

              if (!cartItem) {
                return;
              }

              const deliveryItem = deliveryOrders
                .flatMap(({ items: orderItems }) => orderItems)
                .find(({ sku }) => sku === (cartItem as Offer).sku);

              if (!deliveryItem) {
                return;
              }

              const {
                base_price,
                discount_amount,
                image,
                name,
                paid_bonuses,
                paid_price,
                product_id,
                promocode_discount_amount,
                requested_quantity,
                sku,
                weight,
              } = deliveryItem;

              items.push({
                base_price,
                discount_amount,
                image,
                name,
                paid_bonuses,
                paid_price,
                promocode_discount_amount,
                requested_quantity,
                sku,
                weight,
                product_id: `${product_id}`,
                price_per_unit: cartItem.pricePerUnit,
              });
            });

            const bundles = deliveryOrders.flatMap(
              ({ bundles: orderBundles }) => orderBundles ?? [],
            );

            const sumOfOrders = (
              pick: (order: CartCalculationOrderResponse) => number,
            ) =>
              deliveryOrders.reduce((acc, item) => acc + (pick(item) || 0), 0);

            const result = {
              base_total: isGroupedDelivery
                ? cartStore.cartTotalsPence.base_total
                : base_total,
              discount_total: isGroupedDelivery
                ? cartStore.cartTotalsPence.discount_total
                : discount_total,
              is_delivery_free,
              paid_bonuses: isGroupedDelivery
                ? sumOfOrders(({ paid_bonuses: value }) => value)
                : paid_bonuses,
              paid_total: isGroupedDelivery
                ? cartStore.cartTotalsPence.paid_total
                : paid_total,
              promocode: (!promocodeError && promocode) || null,
              promocode_discount: isGroupedDelivery
                ? sumOfOrders(({ promocode_discount: value }) => value)
                : promocode_discount,
              service_fee: isGroupedDelivery
                ? sumOfOrders(({ service_fee: value }) => value)
                : service_fee,
              should_use_loyalty_points,
              should_use_bonuses,
              mechanic: DeliveryMechanic.ON_DEMAND,
              items,
              ...(bundles.length
                ? {
                    bundles: bundles.map((i) => ({
                      id: i.id,
                      requested_quantity: i.requested_quantity,
                    })),
                  }
                : {}),

              // FIXME: where to get? ETA slot?
              delivery_price: isGroupedDelivery
                ? groupDeliveryPrice
                : paid_price,
              tips_amount: 0,
            };

            if (slotDeliveryDetails) {
              // A slot on a self-pickup leg keeps the PICKUP mechanic (OMS re-derives
              // it anyway; SCHEDULED here would just be misleading telemetry).
              const slotMechanic: DeliveryMechanic =
                checkoutStore.isPickupSelected
                  ? DeliveryMechanic.PICKUP
                  : DeliveryMechanic.SCHEDULED;

              return {
                ...result,
                mechanic: slotMechanic,
                slot_delivery_details: {
                  current_date: slotDeliveryDetails.currentDate,
                  schedule_slot_id: slotDeliveryDetails.scheduleSlotId,
                },
              };
            }

            return result;
          },
        ) ?? [];

      const customerCash =
        (data.hasExactAmount && deliveries[0] && deliveries[0].paid_total) ||
        (isNumber(data.customerCash) && !isNaN(data.customerCash))
          ? data.customerCash * 100
          : 0;

      const recipientEmail = data.email.trim();
      const recipient = {
        full_name: data.fullName,
        phone: data.phone,
        ...(recipientEmail ? { email: recipientEmail } : {}),
      };

      // A breadcrumb rides on every event for the rest of the session, so this
      // carries the shape of the order rather than its contents. The full
      // street address, coordinates and recipient used to be here when it was
      // a single event; keeping them now would spread one customer's details
      // across every unrelated error that followed. What a failed order needs
      // is which warehouse, which method, how much — and whether the fields
      // the API rejects were populated at all.
      trace(DOMAIN.checkout, 'PREREQUEST NEW ORDER', {
        currency: this.currency,
        warehouseCode: this.etaWarehouseCode,
        deliveryMethod: data.deliveryMethod,
        paymentMethod: payment_method,
        paymentSystem: payment_system,
        saveCard: data.saveCard,
        customerCash,
        deliveriesCount: deliveries?.length ?? 0,
        cartOrdersCount: cartStore.deliveriesOrders?.length ?? 0,
        hasRecipientName: Boolean(recipient.full_name),
        hasRecipientEmail: Boolean(recipientEmail),
        hasCoordinates: Boolean(this.userStore.deliveryAddress?.coordinates),
        hasAddressLine: Boolean(data.address),
        city: this.userStore.deliveryAddress?.city ?? '',
        countryId: this.userStore.deliveryAddress?.country ?? '',
      });

      const cardId =
        (checkoutStore.activePaymentSystem === PaymentSystem.amazon ||
          checkoutStore.activePaymentSystem === PaymentSystem.adyen ||
          checkoutStore.activePaymentSystem === PaymentSystem.verifone ||
          (checkoutStore.activePaymentSystem === PaymentSystem.octo &&
            company.isOctoSavedCardsEnabled)) &&
        !checkoutStore.isAddNewCard &&
        checkoutStore.activePaymentMethod !== ETAPaymentMethod.Cash &&
        !isPhonePaymentMethod(
          checkoutStore.activePaymentMethod as ETAPaymentMethod,
        ) &&
        checkoutStore.activePaymentMethod !== ETAPaymentMethod.QR
          ? checkoutStore.paymentsMethodId
          : undefined;

      const flow =
        checkoutStore.activePaymentMethod === ETAPaymentMethod.Card &&
        checkoutStore.activePaymentSystem === PaymentSystem.adyen
          ? { checkoutFlow: 'advanced' }
          : {};

      const deliveryInfo = this.deliveryStore.getOrderDeliveryPayload(
        data.deliveryMethod,
        deliveries.some((item) => item.mechanic === DeliveryMechanic.SCHEDULED)
          ? DeliveryMechanic.SCHEDULED
          : DeliveryMechanic.ON_DEMAND,
      );

      const zone =
        this.etaCalculation?.warehouse.zone[checkoutStore.deliveryMethod];

      const newOrderData = {
        order: {
          customer_id: this.userStore.personalData.id?.toString() ?? '',
          currency: this.currency,
          warehouse_code: this.etaWarehouseCode,
          zone_id: zone?.id,
          ...deliveryInfo,
          client_source: 'App',
          save_card: data.saveCard,
          session_id,
          pickup_point_uuid:
            this.userStore.deliveryAddress?.pickupPointId || undefined,
          // The type the customer picked, so the backend binds the delivery to
          // a zone of that type instead of guessing by coordinates.
          destination_kind: company.hasDeliveryTypeSwitcher
            ? destinationKindForUsage(this.userStore.deliveryAddress?.usage)
            : undefined,
          deliveries,
          payment: {
            phone: data.mobileMoneyPhone,
            payment_method,
            payment_system,
            available_payment_providers:
              this.paymentSystems.size > 0
                ? Array.from(this.paymentSystems.keys())
                : [],
            card_id: checkoutStore.isAddNewCard
              ? undefined
              : cardId || undefined,
            pay_by_saved_card: !!cardId || undefined,
            ...data.additionalData,
            ...flow,
          },
          customerCash,
        },
        recipient,
        address: {
          city: this.userStore.deliveryAddress?.city ?? '',
          country_id: this.userStore.deliveryAddress?.country ?? '',
          region: this.userStore.deliveryAddress?.region ?? '',
          address_1: data.address ?? '',
          address_2: data.address2 ?? '',
          building: data.addressBld ?? '',
          floor: data.addressFloor ?? '',
          apartment: data.addressApt ?? '',
          latitude: this.userStore.deliveryAddress?.coordinates.lat ?? null,
          longitude: this.userStore.deliveryAddress?.coordinates.lng ?? null,
          zip: this.userStore.deliveryAddress?.zip ?? '',
          comment: data.comment,
          instructions: data.instructions ?? null,
          notes: data.notes ?? '',
        },
      };

      const response = await OrderRequests.newOrder(
        newOrderData,
        idempotencyKey,
      );

      this.idempotency.reset();

      this.isLoading = false;
      return response;
    } catch (error) {
      this.idempotency.reset();

      reportFailure(
        DOMAIN.checkout,
        'Failed create new order TOP ERROR NOT AXIOS',
        {
          reason: REASON.checkout.orderCreateFailedNonAxios,
          extras: {
            context: 'newOrder first error',
            error: (error && (error as any)['response']) || error,
          },
        },
      );
      //mainStore.pushAlert('error', i18n.t('errors:unableCreateOrder'));
      if (error instanceof AxiosError) {
        await this.reCalculateDeliveries();

        reportFailure(DOMAIN.checkout, 'Failed create new order', {
          reason: REASON.checkout.orderCreateFailed,
          extras: {
            context: 'newOrder',
            error: (error && (error as any)['response']) || error,
          },
        });

        const { response, status } = error;

        if (!response) {
          this.isLoading = false;
          return null;
        }

        const { data } = response;

        if (data?.data?.errorCode && handleOrderError(data)) {
          this.isLoading = false;
          throw new OrderErrorHandled();
        }

        if (data?.errors?.length) {
          if (checkoutStore.activePaymentSystem === PaymentSystem.verifone) {
            const mainError = data.errors[0];
            const errorFromArray =
              typeof mainError === 'string' ? mainError : mainError?.message;

            let errorMessage = errorFromArray || 'Payment failed';

            if (data?.data?.refusalReason) {
              errorMessage = `${errorMessage} - ${data.data.refusalReason}`;
            } else if (
              data?.data?.resultCode &&
              data.data.resultCode !== 'INVALID_DATA'
            ) {
              errorMessage = `${errorMessage} - ${data.data.resultCode}`;
            }

            errorMessage = errorMessage.replace(/\s*\(PPC\)\s*$/i, '');
            throw new Error(errorMessage);
          }
          if (checkoutStore.activePaymentSystem === PaymentSystem.adyen) {
            if (status === 500 || !data?.data?.resultCode) {
              throw new Error('Unable to create order');
            }
            const mainError = data.errors[0];
            const errorMessage =
              typeof mainError === 'string'
                ? [mainError]
                : [mainError?.message || error.message];

            if (data?.data && data?.data?.resultCode) {
              errorMessage.push(data.data.refusalReason);
            }
            throw new Error(errorMessage.filter(Boolean).join('. '));
          }
          if (checkoutStore.activePaymentSystem === PaymentSystem.pvit) {
            throw new Error(data.errors[0]);
          }
          if (
            data.errors[0] &&
            data.errors[0].toLowerCase() === 'gift out of stock'
          ) {
            mainStore.setIsShowGiftOutStockPopover(true);
            this.isLoading = false;
            return null;
          }
          if (data.errors[0] === 'Promocode is not valid') {
            runInAction(() => {
              cartStore.promocode.message =
                data.data?.payload?.err_code ===
                'DISCOUNT.COUPONS.ONLY_FIRST_ORDER_ALLOWED'
                  ? '' //i18n.t('cartPage:notFirstOrderPromocodeFreeDelivery')
                  : ''; //i18n.t('cartPage:promocodeNotFoundText');
              cartStore.promocode.coupon = null;
              cartStore.promocode.errorType = 'error';
              cartStore.promocode.success = false;
              mainStore.setIsInvalidPromocodePopover(true);
            });
            this.isLoading = false;
            return null;
          }
        }
      }
    } finally {
      this.isLoading = false;
    }
    return null;
  }

  private async newOrderV3(
    data: OrderData,
    payment_method: ETAPaymentMethod,
    payment_system: PaymentSystem | null,
    payment_method_id: number | undefined,
    session_id: string | undefined,
  ): Promise<NewOrderResponse | null> {
    try {
      const idempotencyKey = this.idempotency.ensure();
      const findToken = cartStore.v3FindToken;
      const routeSelection = cartStore.v3RouteSelection;

      if (!findToken) {
        throw new Error('V3 newOrder: missing findToken');
      }
      if (routeSelection.length === 0) {
        throw new Error('V3 newOrder: empty route selection');
      }
      for (const sel of routeSelection) {
        if (!sel.warehouse_id || !sel.template_id) {
          throw new Error('V3 newOrder: invalid route selection entry');
        }
      }

      const tips_amount = mainStore.convertPoundsToPence(this.orderTipsValue);
      if (tips_amount > 0 && routeSelection.length > 1) {
        throw new Error('TIPS_NOT_SUPPORTED_FOR_MULTIORDER_LMS');
      }

      const cardId =
        (checkoutStore.activePaymentSystem === PaymentSystem.amazon ||
          checkoutStore.activePaymentSystem === PaymentSystem.adyen ||
          checkoutStore.activePaymentSystem === PaymentSystem.verifone) &&
        !checkoutStore.isAddNewCard &&
        checkoutStore.activePaymentMethod !== ETAPaymentMethod.Cash &&
        !isPhonePaymentMethod(
          checkoutStore.activePaymentMethod as ETAPaymentMethod,
        ) &&
        checkoutStore.activePaymentMethod !== ETAPaymentMethod.QR
          ? checkoutStore.paymentsMethodId
          : undefined;

      const checkoutFlow =
        checkoutStore.activePaymentMethod === ETAPaymentMethod.Card &&
        checkoutStore.activePaymentSystem === PaymentSystem.adyen
          ? 'advanced'
          : null;

      const selectedItems = cartStore.selectedCartItems.map((item) =>
        toJS(item),
      );
      const items: V3OrderItemInput[] = [];
      const bundles: V3OrderBundleInput[] = [];
      selectedItems.forEach((item) => {
        if ('sku' in item && item.sku) {
          items.push({
            sku: (item as Offer).sku,
            requested_quantity: item.count,
          });
        } else {
          bundles.push({
            id: item.id as number,
            requested_quantity: item.count,
          });
        }
      });

      const firstDelivery = cartStore.deliveriesOrders[0];
      const totalForCash = firstDelivery?.paid_total ?? 0;
      const customerCash =
        (data.hasExactAmount && totalForCash) ||
        (isNumber(data.customerCash) && !isNaN(data.customerCash))
          ? data.customerCash * 100
          : 0;

      const { deliveryAddress, personalData, loyaltyPointsStore } =
        this.userStore;

      const fullName =
        data.fullName?.trim() ||
        `${personalData.firstName ?? ''} ${personalData.lastName ?? ''}`.trim();

      const payment: V3PaymentInput = {
        payment_method,
        payment_system,
        payment_method_id: payment_method_id ?? null,
        session_id: session_id ?? null,
        pay_by_saved_card: !!cardId,
        card_id: checkoutStore.isAddNewCard
          ? null
          : ((cardId as string | undefined) ?? null),
        phone: data.mobileMoneyPhone ?? null,
        available_payment_providers:
          this.paymentSystems.size > 0
            ? Array.from(this.paymentSystems.keys())
            : [],
        checkoutFlow,
        ...(data.additionalData ?? {}),
      };

      const totals = cartStore.v3Totals;
      if (!totals) {
        throw new Error('V3 newOrder: missing calculated totals');
      }

      const zoneId =
        this.etaCalculation?.warehouse?.zone?.[checkoutStore.deliveryMethod]
          ?.id ?? '';

      // Pickup orders may carry no customer address; the point's own is sent.
      const selectedPup = cartStore.v3AvailablePups.find(
        (pup) => pup.id === cartStore.v3SelectedPupId,
      );

      const addressParts = splitAddressLine(
        data.address ||
          deliveryAddress?.address1 ||
          selectedPup?.location?.addressText ||
          '',
        deliveryAddress?.city,
        data.addressBld || deliveryAddress?.addressBld,
      );

      const v3Request: V3NewRequest = {
        idempotency_key: idempotencyKey,
        recipient: {
          email: data.email.trim(),
          full_name: fullName,
          phone: data.phone,
        },
        address: {
          address_1: addressParts.street,
          address_2: data.address2 || null,
          city: deliveryAddress?.city ?? selectedPup?.entityData?.city ?? '',
          comment: data.comment || null,
          country_id: deliveryAddress?.country ?? company.config.countryCode,
          latitude:
            deliveryAddress?.coordinates.lat ?? selectedPup?.location.lat ?? 0,
          longitude:
            deliveryAddress?.coordinates.lng ?? selectedPup?.location.lon ?? 0,
          notes: data.notes || null,
          region: deliveryAddress?.region ?? '',
          zip: deliveryAddress?.zip ?? '',
          building: addressParts.building || null,
          floor: data.addressFloor || null,
          apartment: data.addressApt || null,
        },
        destination: cartStore.v3SelectedPupId
          ? { kind: 'PUP', pup_id: cartStore.v3SelectedPupId }
          : { kind: 'CUSTOMER' },
        order: {
          currency: this.currency.toUpperCase(),
          zone_id: zoneId,
          items,
          bundles,
          should_use_bonuses: false,
          should_use_loyalty_points: loyaltyPointsStore.useLoyaltyPoints,
          customerCash,
          save_card: data.saveCard,
          ...(cartStore.promocode.coupon?.code
            ? { promocode: cartStore.promocode.coupon.code }
            : {}),
          ...(tips_amount > 0 ? { tips_amount } : {}),
        },
        totals: pickV3NewOrderTotals(totals),
        payment,
        routeSelection: {
          findToken,
          orders: routeSelection.map((s) => ({
            warehouse_id: s.warehouse_id,
            warehouse_code: s.warehouse_code,
            template_id: s.template_id,
            ...(s.slot_delivery_details
              ? { slot_delivery_details: s.slot_delivery_details }
              : {}),
          })),
        },
      };

      trace(DOMAIN.checkout, 'V3 NEW ORDER REQUEST', { request: v3Request });

      const v3Response = await V3Requests.newOrder(v3Request, idempotencyKey);

      runInAction(() => {
        this.idempotency.reset();
      });

      this.isLoading = false;
      return adaptV3NewToV2(v3Response);
    } catch (error) {
      const code = (error as AxiosError)?.response?.data as
        | { errors?: string[]; error_code?: string }
        | undefined;
      const errors = code?.errors ?? [];
      const recalcSignals = [
        'INVALID_ROUTE_SELECTION',
        'CALCULATE_TOTALS_MISMATCH',
        'Calculated totals do not match submitted totals',
        'Selected route template is not available for reservation',
        'LMS V3 calculation failed',
      ];
      const shouldRecalculate =
        recalcSignals.some((s) => errors.some((e) => e.includes(s))) ||
        (code?.error_code != null && recalcSignals.includes(code.error_code));

      if (shouldRecalculate) {
        await cartStore.calculateCart();
      }

      reportFailure(DOMAIN.checkout, 'V3 new order failed', {
        reason: REASON.checkout.orderCreateFailed,
        extras: {
          context: 'newOrderV3',
          error: (error as AxiosError)?.response ?? error,
        },
      });

      this.isLoading = false;
      throw error;
    }
  }

  async reCalculateDeliveries() {
    if (this.isETALoading) return;

    if (!company.hasNewLogistics) {
      checkoutStore.slotsChangedEventStore.preserveDeliveryInfo(
        this.etaCalculation?.duration.range,
      );
    }
    await this.requestETA();
    const result = checkoutStore.checkSlotExpirationOfExistingDeliveries(
      checkoutStore.sortedDeliverySlotsList,
    );
    if (!company.hasNewLogistics) {
      checkoutStore.slotsChangedEventStore.checkDifferenceWithPreviousDeliveries();
    }

    return result;
  }

  async check3dsStatus(orderId: string) {
    const res = await OrderRequests.get3dsStatus(orderId);

    this.set3dsUrl(res['3ds_url']);

    return res;
  }

  async checkOrderPayment(orderId: string): Promise<{
    address: OrderAddress | null;
    status: OrderStatus | null;
    received_bonuses: number | null;
  }> {
    try {
      const {
        order: { address, status, received_bonuses },
      } = await OrderRequests.getOrder(orderId);
      return { address, status, received_bonuses };
    } catch (error) {
      if (error instanceof AxiosError) {
        const { response } = error;

        if (!response) {
          return { address: null, status: null, received_bonuses: null };
        }
        reportFailure(DOMAIN.checkout, 'Failed create new order', {
          reason: REASON.checkout.orderCreateFailed,
          extras: {
            context: 'checkOrderPayment',
            error: response,
          },
        });
      }
    }

    return { address: null, status: null, received_bonuses: null };
  }

  async orderCancel(orderId: string) {
    try {
      await OrderRequests.orderCancel(orderId);
    } catch (e) {
      console.error(e);
    }
  }

  async orderIdempotencyCancel(): Promise<boolean> {
    if (!this.idempotency.key || !this.userStore.isAuthorized) {
      return true;
    }

    try {
      await OrderRequests.orderIdempotencyCancel({
        idempotency_key: this.idempotency.key,
      });
      this.idempotency.reset();

      return true;
    } catch (e) {
      console.error(e);
    }

    return false;
  }

  async requestOrders(): Promise<OrderShort[]> {
    if (!this.userStore.personalData.id) {
      this.orderList = null;
      return [];
    }

    this.isLoading = true;
    try {
      const { orders } = await OrderRequests.getOrders({
        filter: {
          customer_id: this.userStore.personalData.id,
        },
        options: { page: 1 },
      });
      runInAction(() => {
        this.orderList = orders
          .filter((order) => order.cancel_reason !== 'CANCEL_BY_USER')
          .map((order) => this.calcOrderItems(order));
      });
      return this.orderList ?? [];
    } catch (error) {
      error && console.error(error);
    } finally {
      this.isLoading = false;
    }
    return [];
  }

  async requestPaginatedOrders(payload?: {
    page?: number;
    limit?: number;
    filters?: Record<string, string | string[]>;
  }): Promise<
    Nullable<{ result: OrderShort[]; hasNext: boolean; totalCount: number }>
  > {
    const { page = 1, limit = 25, filters } = payload || {};

    if (!this.userStore.personalData.id) {
      return null;
    }

    try {
      const { orders, total_count } = await OrderRequests.getOrders({
        filter: {
          customer_id: this.userStore.personalData.id,
          ...filters,
        },
        options: { page },
      });
      const totalPages = Math.ceil(total_count / limit);
      return {
        result: orders,
        hasNext: totalPages > page,
        totalCount: total_count,
      };
    } catch (error) {
      error && console.error(error);
    }
    return null;
  }

  async fetchOrders() {
    if (!this.userStore.personalData.id) {
      return;
    }
    try {
      const { orders, total_count } = await OrderRequests.getOrders({
        filter: {
          customer_id: this.userStore.personalData.id,
        },
      });
      let successfulOrders: OrdersResponse;
      if (total_count) {
        successfulOrders = await OrderRequests.getOrders({
          filter: {
            customer_id: this.userStore.personalData.id,
            status: [
              'created',
              'reserved',
              'confirmed',
              'delivery_created',
              'wh_created',
              'picking',
              'ready_to_ship',
              'in_delivery',
              'in_logistic',
              'delivered',
              'delivered_partially',
              'finished',
            ],
          },
        });
      }
      runInAction(() => {
        this.orderList = orders
          .filter((order) => order.cancel_reason !== 'CANCEL_BY_USER')
          .map((order) => this.calcOrderItems(order));
        mainStore.analytics.totalSpent = mainStore.convertPenceToPounds(
          (successfulOrders?.orders || []).reduce(
            (sum, order) => sum + (order.paid_total || 0),
            0,
          ),
        );
        mainStore.analytics.totalOrders = total_count;
        mainStore.analytics.totalSuccessfulOrders =
          successfulOrders?.total_count || 0;
        if (orders.length) {
          // @ts-expect-error FIXME: migrate to noUncheckedIndexedAccess: true
          mainStore.analytics.lastOrderCreatedAt = orders[0].created_at;
          mainStore.analytics.firstOrderCreatedAt =
            // @ts-expect-error FIXME: migrate to noUncheckedIndexedAccess: true
            orders[orders.length - 1].created_at;
        }
        mainStore.sendToRN('setUserProperties', {
          'Commerce: total spent': mainStore.analytics.totalSpent,
        });
        mainStore.sendToRN('setUserProperties', {
          'Commerce: total orders': total_count,
        });
        if (orders.length) {
          mainStore.sendToRN('setUserProperties', {
            'Commerce: first order date':
              mainStore.analytics.firstOrderCreatedAt,
          });
        }
      });
    } catch (error) {
      error && console.error(error);
    }
  }

  async requestOrder(id: string): Promise<Order | OrderShort | null> {
    if (!id) {
      return null;
    }
    if (this.orderList && this.orderList.length) {
      const order = this.getOrderById(id);
      if (order && order.public_status === 'delivered' && order.has_feedback) {
        return order;
      }
    }
    try {
      const { order } = await OrderRequests.getOrder(id);
      return this.calcOrderItems(order);
    } catch (e) {
      await this.errorHandler(
        e as AxiosError<ApiErrorResponse>,
        'requestOrder',
      );
    }
    return null;
  }

  async requestPayments(deliveryZoneId?: string): Promise<boolean> {
    if (!this.userStore.isAuthorized) {
      return false;
    }

    if (this.paymentSystems.size === 0) {
      return false;
    }

    try {
      const res = await OrderRequests.getPayments(
        Array.from(this.paymentSystems.keys()).filter(
          (system) => system !== PaymentSystem.cash,
        ),
        deliveryZoneId,
      );

      if (!res) {
        return false;
      }

      let cardsToSet;

      if (company.useAwsPaymentMethod) {
        cardsToSet = this.prepearAmazonPayments(res as AwsPayment[]);
      } else if (company.name === CompanyName.Heimkaup) {
        cardsToSet = this.prepearVerifonePayments(
          res.storedPaymentMethods as VerifonePaymentCard[],
        );
      } else if ('paymentMethods' in (res as object)) {
        cardsToSet = this.prepearAdyenPayments(
          (res as PaymentMethodsResponse).storedPaymentMethods,
        );
        this.paymentMethods = (res as PaymentMethodsResponse).paymentMethods;
      } else if (company.is('mcosmetic')) {
        cardsToSet = this.prepearOctoPayments(res as OctoPaymentMethod[]);
      } else {
        cardsToSet = res;
      }

      this.setPaymentCards(cardsToSet as Payment[]);
      return true;
    } catch (e) {
      await this.errorHandler(
        e as AxiosError<ApiErrorResponse>,
        'requestPayments',
      );
    }
    return false;
  }

  prepearAmazonPayments(amazonCards: AwsPayment[]): Payment[] {
    return amazonCards.map((item) => ({
      card: {
        brand: '',
        checks: {
          address_line1_check: null,
          address_postal_code_check: null,
          cvc_check: null,
        },
        country: '',
        exp_month: 0,
        exp_year: 0,
        fingerprint: '',
        last4: item.public_identifier.slice(-4),
      },
      id: item.id,
      paymentToken: item.payment_system_id,
      metadata: {
        is_default: `${item.is_default}`,
      },
    }));
  }

  prepearAdyenPayments(
    payments: PaymentMethodsResponse['storedPaymentMethods'],
  ): Payment[] {
    return (
      payments?.map((payment) => ({
        id: payment.id,
        card: {
          brand: payment.brand || '',
          checks: {
            address_line1_check: null,
            address_postal_code_check: null,
            cvc_check: null,
          },
          country: '',
          exp_month: Number(payment.expiryMonth) || 0,
          exp_year: Number(payment.expiryYear) || 0,
          fingerprint: '',
          last4: payment.lastFour || '',
        },
        paymentToken: payment.storedPaymentMethodId || null,
        metadata: {
          is_default:
            'isDefault' in payment
              ? (payment as unknown as { isDefault: boolean }).isDefault
                ? 'true'
                : 'false'
              : 'false',
        },
      })) || []
    );
  }

  prepearVerifonePayments(verifoneCards: VerifonePaymentCard[]): Payment[] {
    return verifoneCards.map((card) => ({
      id: card.id,
      card: {
        brand: card.card_brand
          ? card.card_brand.toLocaleLowerCase().replace(/\s/g, '')
          : '',
        checks: {
          address_line1_check: null,
          address_postal_code_check: null,
          cvc_check: null,
        },
        country: card.metadata?.token_details?.issuer_country || '',
        exp_month: card.card_exp_month || 0,
        exp_year: card.card_exp_year || 0,
        fingerprint: card.stored_credential_reference || '',
        last4: card.card_last_four || '',
      },
      paymentToken: card.provider_token || null,
      metadata: {
        is_default: card.is_default || card.isDefault ? 'true' : 'false',
      },
    }));
  }

  prepearOctoPayments(methods: OctoPaymentMethod[]): Payment[] {
    return methods.map((m) => ({
      id: m.id,
      paymentToken: null,
      card: {
        brand: m.card_brand,
        checks: {
          address_line1_check: null,
          address_postal_code_check: null,
          cvc_check: null,
        },
        country: '',
        exp_month: m.card_exp_month,
        exp_year: m.card_exp_year,
        fingerprint: '',
        last4: m.card_last_four,
      },
      metadata: { is_default: String(m.is_default) },
    }));
  }

  createIntent(): Promise<Payment | AxiosError> {
    return OrderRequests.createIntent().catch((e) =>
      this.errorHandler(e, 'createIntent'),
    );
  }

  addPayment(id: string): Promise<Payment | AxiosError> {
    return OrderRequests.addPayment({ payment_method_id: id }).catch((e) =>
      this.errorHandler(e, 'addPayment'),
    );
  }

  deletePayment(
    id: string,
    deliveryZoneId?: string,
  ): Promise<Payment | AxiosError> {
    return OrderRequests.deletePayment({
      payment_method_id: id,
      deliveryZoneId,
    }).catch((e) => this.errorHandler(e, 'deletePayment'));
  }

  setDefaultPayment(
    id: string,
    deliveryZoneId?: string,
  ): Promise<Payment | AxiosError> {
    return OrderRequests.setDefaultPayment({
      payment_method_id: id,
      deliveryZoneId,
    }).catch((e) => this.errorHandler(e, 'setDefaultPayment'));
  }

  async addFeedback(orderId: string, data: RateInfo) {
    const ratingRate = data.rating;

    if (!ratingRate) {
      return;
    }

    const feedBackID = await this.newFeedback(orderId, ratingRate);

    if (feedBackID && Object.keys(data).length > 1) {
      return this.fillFeedback(orderId, feedBackID, {
        rating: ratingRate,
        delivery_rating: data.deliveryRating ?? -1,
        product_quality_rating: data.productQualityRating ?? -1,
        product_variety_rating: data.productVarietyRating ?? -1,
        comment: data.comment ?? '',
      });
    }

    return true;
  }

  async newFeedback(orderId: string, rating: number): Promise<string> {
    try {
      const { id } = await OrderRequests.newFeedback({
        rating,
        order_id: orderId,
      });

      analyticsService.track(AnalyticsEvent.RATE_ORDER_GENERAL, {
        order_id: orderId,
        score: rating,
      });

      analyticsService.track(AnalyticsEvent.RATE_ORDER_GENERAL_FIREBASE, {
        order_id: orderId,
        score: rating,
      });

      return id;
    } catch (e) {
      await this.errorHandler(e as AxiosError<ApiErrorResponse>, 'newFeedback');
    }
    return '';
  }

  async fillFeedback(
    orderId: string,
    feedbackId: string,
    data: FillFeedbackRequest,
  ): Promise<boolean> {
    const {
      delivery_rating,
      product_variety_rating,
      product_quality_rating,
      comment,
      rating,
    } = data;

    mainStore.sendToRN('setUserProperties', {
      'Commerce: last order rating': rating,
    });
    mainStore.sendToRN('setUserProperties', {
      'Commerce: last order delivery rating': delivery_rating,
    });
    mainStore.sendToRN('setUserProperties', {
      'Commerce: last order product variety rating': product_variety_rating,
    });
    mainStore.sendToRN('setUserProperties', {
      'Commerce: last order product quality rating': product_quality_rating,
    });
    analyticsService.track(AnalyticsEvent.RATE_ORDER_DETAILED, {
      order_id: orderId,
      delivery: delivery_rating,
      product_variety: product_variety_rating,
      product_quality: product_quality_rating,
      comment_provided: !!comment.length,
    });
    analyticsService.track(AnalyticsEvent.RATE_ORDER_DETAILED_FIREBASE, {
      order_id: orderId,
      delivery: delivery_rating,
      product_variety: product_variety_rating,
      product_quality: product_quality_rating,
      comment_provided: !!comment.length,
    });

    try {
      await OrderRequests.fillFeedback(feedbackId, data);

      return true;
    } catch (e) {
      await this.errorHandler(
        e as AxiosError<ApiErrorResponse>,
        'fillFeedback',
      );
    }

    return false;
  }

  async fetchGifts(): Promise<boolean> {
    try {
      const { data } = await OrderRequests.getGifts(
        this.etaCalculation?.warehouse.code || '',
      );
      if (!data) {
        this.setGift(null);
        if (this.activeGift) {
          mainStore.setIsShowGiftOutStockPopover(true);
          return false;
        }
        return true;
      }
      const isMinimumOrderAmountDiff = !!(
        this.gift &&
        this.gift.minimum_order_amount !== data.minimum_order_amount
      );
      this.setGift(data);
      this.setIsAuthInCart(false);
      if (this.activeGift) {
        if (
          (isMinimumOrderAmountDiff &&
            mainStore.convertPoundsToPence(cartStore.finalPrice) <
              mainStore.convertPoundsToPence(data.minimum_order_amount)) ||
          !data.gift_items.some((item) => item.id === this.activeGift?.id)
        ) {
          mainStore.setIsShowGiftOutStockPopover(true);
          return false;
        }
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        const { response } = error;
        if (!response) {
          return false;
        }
        if (
          this.isAuthInCart &&
          response.data?.errors?.length &&
          response.data.errors[0] &&
          response.data.errors[0].toLowerCase() === 'gift already received'
        ) {
          mainStore.setIsShowGiftAlreadyReceivedPopover(true);
        }
        this.setGift(null);
        if (this.activeGift) {
          mainStore.setIsShowGiftOutStockPopover(true);
          return false;
        }
      }
    }
    return true;
  }

  async payTip(
    orderId: string,
    payAmount: string,
    isSaveCard: boolean,
  ): Promise<string> {
    try {
      const { client_secret } = await OrderRequests.payTip(orderId, {
        amount: mainStore.convertPoundsToPence(payAmount),
        save_card: isSaveCard,
        currency: this.currency,
        email: this.userStore.personalData.email || '',
      });
      return client_secret || '';
    } catch (error) {
      error && console.error(error);
    }
    return '';
  }

  async checkPayedTip(orderId: string): Promise<boolean> {
    if (!this.userStore.isAuthorized) {
      return true;
    }
    try {
      const { status } = await OrderRequests.checkPayedTip(orderId);
      return status === 'received';
    } catch (error) {
      if (error instanceof AxiosError) {
        if (error.response?.status === 400) {
          return false;
        }
        error && console.error(error);
      }
    }
    return true;
  }

  async checkIsFirstOrder(isForce?: boolean) {
    if (this.isFirstOrder !== null && !isForce) {
      return;
    }

    if (!this.userStore.personalData.id) {
      this.setIsFirstOrder(true);

      return;
    }

    try {
      // gets all statuses exclude canceled, delivery_failed, because such statuses are ignored on backend
      const { total_count } = await OrderRequests.getOrders({
        filter: {
          customer_id: this.userStore.personalData.id ?? '',
          status: [
            'created',
            'reserved',
            'confirmed',
            'delivery_created',
            'wh_created',
            'picking',
            'ready_to_ship',
            'in_delivery',
            'in_logistic',
            'delivered',
            'delivered_partially',
            'finished',
          ],
        },
        options: {
          page: 1,
          limit: 1,
        },
      });

      this.setIsFirstOrder(total_count === 0);
      return total_count === 0;
    } catch {
      this.setIsFirstOrder(true);
    }
  }

  requestCurrency() {
    OrderRequests.getCurrency()
      .then(({ data: { currency = '', digits_after_coma = 2 } }) => {
        this.setCurrency(currency);
        mainStore.setDigitsAfterComma(digits_after_coma);
      })
      .catch((e) => this.errorHandler(e, 'requestCurrency'));
  }

  /**
   * Loads the payment systems offered for a delivery zone.
   *
   * Callers asking for the same zone share one request: the request cycle, the
   * checkout and its content all ask on mount, and three racing responses could
   * otherwise land out of order.
   */
  requestPaymentSystems(deliveryZoneId?: string): Promise<void> {
    if (!this.userStore.isAuthorized) {
      return Promise.resolve();
    }

    const key = deliveryZoneId ?? '';
    if (this._paymentSystemsRequest?.key === key) {
      return this._paymentSystemsRequest.promise;
    }

    const promise = this._loadPaymentSystems(deliveryZoneId).finally(() => {
      this._paymentSystemsRequest = null;
    });
    this._paymentSystemsRequest = { key, promise };

    return promise;
  }

  private async _loadPaymentSystems(deliveryZoneId?: string): Promise<void> {
    this.setIsPaymentSystemsLoading(true);

    for (
      let attempt = 0;
      attempt <= PAYMENT_SYSTEMS_RETRY_DELAYS.length;
      attempt++
    ) {
      try {
        const systems = await OrderRequests.getPaymentSystems(deliveryZoneId);

        runInAction(() => {
          this.setPaymentSystems(systems);
          this.setArePaymentSystemsLoaded(true);
          this.setIsPaymentSystemsLoading(false);
        });
        return;
      } catch (e) {
        const retryDelay = PAYMENT_SYSTEMS_RETRY_DELAYS[attempt];
        if (retryDelay !== undefined) {
          await new Promise((resolve) => setTimeout(resolve, retryDelay));
          continue;
        }

        // The previously loaded list is deliberately kept. Clearing it turned a
        // single failed request into a checkout that could never initialise —
        // `init()` is gated on the list being non-empty, and after the page is
        // up nothing asks for it again.
        this.setIsPaymentSystemsLoading(false);
        this.errorHandler(
          e as AxiosError<ApiErrorResponse>,
          'paymentSystems',
        ).catch(() => void 0);
        return;
      }
    }
  }

  // Errors
  errorHandler = (
    error: AxiosError<ApiErrorResponse>,
    context: string,
  ): Promise<AxiosError> => mainStore.errorHandler(error, context);

  clearOrderList() {
    this.orderList = [];
  }

  private _clearOrdersPollingPayload() {
    if (this._ordersPollingTimeoutRef) {
      clearTimeout(this._ordersPollingTimeoutRef);
      this._ordersPollingTimeoutRef = null;
    }
  }
  private _startOrdersPolling(opts?: { timeout?: number }) {
    const { timeout = 30 * SECOND } = opts || {};

    this._clearOrdersPollingPayload();

    const handler = async () => {
      const orders = await this.requestOrders();

      const onlyActiveOrders =
        orders.filter((order) =>
          ACTIVE_ORDER_STATUS_CRITERIA.includes(order.public_status),
        ) || [];

      if (onlyActiveOrders.length === 0) {
        this.setOrdersPollingEnabled(false);
        return;
      }

      this._ordersPollingTimeoutRef = setTimeout(handler, timeout);
    };

    handler();
  }

  destroy(): void {
    // Clean up any resources or listeners here if necessary in future
    // Currently, no resources need to be explicitly released
  }
}
